import { useNode } from "@craftjs/core";
import React, { useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useResponsive } from "../../hooks/useResponsive";
import { selectedOutlineSx, labelStyle } from "../../config/settingsStyles";
import { TextField, ColorField, SliderField, CheckboxField, SettingsSection } from "../ui/form/SettingsField";

interface ContactFormProps {
  formAction?: string;
  submitText?: string;
  successMessage?: string;
  showPhone?: boolean;
  showSubject?: boolean;
  backgroundColor?: string;
  borderRadius?: number;
  padding?: number;
}

export const ContactForm = ({
  formAction = "#",
  submitText = "Send Message",
  successMessage = "Thank you! We will contact you soon.",
  showPhone = true,
  showSubject = false,
  backgroundColor = "#ffffff",
  borderRadius = 12,
  padding = 32,
}: ContactFormProps) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useResponsive(containerRef as React.RefObject<HTMLElement>);

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 3000);
  };

  const safePadding = padding || 32;

  const inputStyles = {
    width: "100%",
    px: isMobile ? "12px" : "16px",
    py: isMobile ? "10px" : "12px",
    border: "1px solid #d1d5db",
    borderRadius: 2,
    fontSize: isMobile ? "0.875rem" : "1rem",
    "&:focus": {
      outline: "none",
      boxShadow: "0 0 0 2px rgba(59, 130, 246, 0.5)",
      borderColor: "transparent",
    },
  };

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = ref;
        if (ref) connect(drag(ref));
      }}
      sx={{ ...(selected && selectedOutlineSx) }}
    >
      <Box
        component="form"
        action={formAction}
        method="POST"
        onSubmit={handleSubmit}
        sx={{
          bgcolor: backgroundColor,
          borderRadius: `${borderRadius}px`,
          p: isMobile ? `${Math.max(16, safePadding * 0.5)}px` : `${safePadding}px`,
          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
        }}
      >
        {isSubmitted ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography sx={{ fontSize: "2.25rem", mb: 2, display: "block" }}>✅</Typography>
            <Typography
              sx={{
                color: "#16a34a",
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {successMessage}
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: 2,
                mb: 2,
              }}
            >
              <Box>
                <Typography
                  component="label"
                  sx={{
                    display: "block",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: "#374151",
                    mb: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  Name{" "}
                  <Box component="span" sx={{ color: "#ef4444" }}>
                    *
                  </Box>
                </Typography>
                <Box component="input" type="text" name="name" required placeholder="Your name" sx={inputStyles} />
              </Box>
              <Box>
                <Typography
                  component="label"
                  sx={{
                    display: "block",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: "#374151",
                    mb: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  Email{" "}
                  <Box component="span" sx={{ color: "#ef4444" }}>
                    *
                  </Box>
                </Typography>
                <Box
                  component="input"
                  type="email"
                  name="email"
                  required
                  placeholder="your@email.com"
                  sx={inputStyles}
                />
              </Box>
            </Box>

            {showPhone && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  component="label"
                  sx={{
                    display: "block",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: "#374151",
                    mb: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  Phone
                </Typography>
                <Box component="input" type="tel" name="phone" placeholder="Your phone number" sx={inputStyles} />
              </Box>
            )}

            {showSubject && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  component="label"
                  sx={{
                    display: "block",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: "#374151",
                    mb: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  Subject
                </Typography>
                <Box component="input" type="text" name="subject" placeholder="Subject" sx={inputStyles} />
              </Box>
            )}

            <Box sx={{ mb: 2 }}>
              <Typography
                component="label"
                sx={{
                  display: "block",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "#374151",
                  mb: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Message{" "}
                <Box component="span" sx={{ color: "#ef4444" }}>
                  *
                </Box>
              </Typography>
              <Box
                component="textarea"
                name="message"
                rows={4}
                required
                placeholder="Your message..."
                sx={{
                  ...inputStyles,
                  resize: "none",
                }}
              />
            </Box>

            <Box
              component="button"
              type="submit"
              sx={{
                width: "100%",
                py: isMobile ? "10px" : "12px",
                px: isMobile ? "16px" : "24px",
                color: "#ffffff",
                fontWeight: 500,
                fontSize: isMobile ? "0.875rem" : "1rem",
                borderRadius: 2,
                border: "none",
                cursor: "pointer",
                transition: "opacity 0.2s",
                background:
                  "linear-gradient(135deg, var(--color-primary, #3b82f6) 0%, var(--color-secondary, #10b981) 100%)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                "&:hover": { opacity: 0.9 },
              }}
            >
              {submitText}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

const ContactFormSettings = () => {
  const {
    actions: { setProp },
    formAction,
    submitText,
    successMessage,
    showPhone,
    showSubject,
    backgroundColor,
    borderRadius,
    padding,
  } = useNode((node) => ({
    formAction: node.data.props.formAction,
    submitText: node.data.props.submitText,
    successMessage: node.data.props.successMessage,
    showPhone: node.data.props.showPhone,
    showSubject: node.data.props.showSubject,
    backgroundColor: node.data.props.backgroundColor,
    borderRadius: node.data.props.borderRadius,
    padding: node.data.props.padding,
  }));

  return (
    <SettingsSection>
      <div>
        <TextField
          label="Form Action URL"
          value={formAction || "#"}
          onChange={(v) => setProp((props: ContactFormProps) => (props.formAction = v))}
          placeholder="https://formspree.io/f/..."
        />
        <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "4px" }}>
          Use Formspree, Google Forms, or similar service
        </p>
      </div>

      <TextField
        label="Submit Button Text"
        value={submitText || "Send Message"}
        onChange={(v) => setProp((props: ContactFormProps) => (props.submitText = v))}
      />

      <TextField
        label="Success Message"
        value={successMessage || ""}
        onChange={(v) => setProp((props: ContactFormProps) => (props.successMessage = v))}
      />

      <div>
        <label style={labelStyle}>Form Fields</label>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <CheckboxField
            label="Show Phone Field"
            checked={showPhone !== false}
            onChange={(v) => setProp((props: ContactFormProps) => (props.showPhone = v))}
          />
          <CheckboxField
            label="Show Subject Field"
            checked={showSubject || false}
            onChange={(v) => setProp((props: ContactFormProps) => (props.showSubject = v))}
          />
        </div>
      </div>

      <ColorField
        label="Background Color"
        value={backgroundColor || "#ffffff"}
        onChange={(v) => setProp((props: ContactFormProps) => (props.backgroundColor = v))}
      />

      <SliderField
        label="Border Radius"
        value={borderRadius || 12}
        onChange={(v) => setProp((props: ContactFormProps) => (props.borderRadius = v))}
        min={0}
        max={24}
        unit="px"
      />

      <SliderField
        label="Padding"
        value={padding || 32}
        onChange={(v) => setProp((props: ContactFormProps) => (props.padding = v))}
        min={16}
        max={64}
        unit="px"
      />
    </SettingsSection>
  );
};

ContactForm.craft = {
  displayName: "Contact Form",
  props: {
    formAction: "#",
    submitText: "Send Message",
    successMessage: "Thank you! We will contact you soon.",
    showPhone: true,
    showSubject: false,
    backgroundColor: "#ffffff",
    borderRadius: 12,
    padding: 32,
  },
  related: {
    settings: ContactFormSettings,
  },
};
